import { Select, Table, TableColumnsType } from "antd";
import _ from "lodash";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import config from "../../../lib/config";
import exportTitleTable from "../../../lib/exportTitleTable";
import { IExamStatsByLevel } from "../../../models/statistics.model";
import { RootState } from "../../../store/rootReducer";
import DownloadButton from "../../DownloadButton";
import Pagination from "../../Pagination";
import moment from "moment";

interface IProps {
  rawData: IExamStatsByLevel[]|undefined;
  excelParam: {excelCampusCodes: string[]}|undefined;
  gradeCode: string|undefined;
  setGradeCode: (gradeCode: string) => void;
}

const ExamStatsByLevel: FC<IProps> = ({
  rawData,
  excelParam,
  gradeCode,
  setGradeCode,
}) => {
  const { t } = useTranslation();
  const { campusData } = useSelector((state: RootState) => state.campusData);

  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // SY0006 = 학년
  const SY0006 = commonCodes.find((item) => item.commonCode === 'SY0006')?.detailCode;

  const [data, setData] = useState<IExamStatsByLevel[]>([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (rawData) {
      let _data: any = _.cloneDeep(rawData);
      if (rawData?.length) {
        _data.forEach((item: any, idx: number) => {
          item.subjectInfo?.forEach((subitem: any) => {
            item[subitem.subjectName] = subitem.subjectScore;
          });
          item.rowIdx = idx;
        });
      }

      setData(_data);
    }
  }, [rawData]);

  const getColumns = () => {
    const firstColumns: TableColumnsType<IExamStatsByLevel> = [
      {
        title: t('common.menu.campus'),
        dataIndex: 'campusKorName',
        key: 'campusKorName',
        align: 'center',
      },
      {
        title: t('statistics.dttt.column1'),
        dataIndex: 'courseName',
        key: 'courseName',
        align: 'center',
      },
      {
        title: t('class.class.column3'),
        dataIndex: 'examName',
        key: 'examName',
        align: 'center',
      },
      {
        title: 'Overall',
        dataIndex: 'overall',
        key: 'overall',
        align: 'center',
        render: value => Math.floor(value),
      },
    ];
    const lastColumns: TableColumnsType<IExamStatsByLevel> = [
      {
        title: t('statistics.placementTest.column1'),
        dataIndex: 'averageTimeRequired',
        key: 'averageTimeRequired',
        align: 'center',
        render: value => Math.round(value),
      },
      {
        title: t('statistics.placementTest.column2'),
        dataIndex: 'totalRequireCount',
        key: 'totalRequireCount',
        align: 'center',
      },
      {
        title: t('statistics.placementTest.column3'),
        dataIndex: 'participantCount',
        key: 'participantCount',
        align: 'center',
      },
      {
        title: t('statistics.placementTest.column4'),
        dataIndex: 'nonParticipantCount',
        key: 'nonParticipantCount',
        align: 'center',
      },
      {
        title: t('statistics.placementTest.column5'),
        dataIndex: 'participantRate',
        key: 'participantRate',
        align: 'center',
        render: value => Math.round(value),
      },
    ];
    const middleColumns: TableColumnsType<IExamStatsByLevel> = [];

    if (rawData?.length) {
      //과목 정렬 처리
      let subjectOrderMap : Map<number, any> = new Map<number, any>();

      // @ts-ignore
      rawData.at(0).subjectInfo?.forEach(item => {
        if(item.subjectName === "Listening") {
          subjectOrderMap.set(1, item.subjectName);
        } else if(item.subjectName === "Reading") {
          subjectOrderMap.set(2, item.subjectName);
        } else if(item.subjectName === "Vocabulary") {
          subjectOrderMap.set(3, item.subjectName);
        } else if(item.subjectName === "Grammar") {
          subjectOrderMap.set(4, item.subjectName);
        } else {
          subjectOrderMap.set(5, item.subjectName);
        }
      });

      const sortedSubjectInfo = new Map([...subjectOrderMap.entries()].sort((a,b) => a[0] - b[0]));

      sortedSubjectInfo.forEach(item => {
        middleColumns.push({
          title: item,
          dataIndex: item,
          key: item,
          align: 'center',
          render: value => Math.round(value),          
        });
      });
    }

    return _.concat(firstColumns, middleColumns, lastColumns);
  }
  
  const onExportTable = () => {
    const currentDate = moment(new Date());

    let excelTitle = t('statistics.regularTest.1');
    let arrSubTitle = [];
    let campusNames = '';

    if(excelParam?.excelCampusCodes) {
      if(excelParam.excelCampusCodes.length === 0) {
        campusNames = t('common.text.all');
      } else {
        excelParam.excelCampusCodes.forEach((item) => {
          if(campusNames !== '') {
            campusNames += ', '  
          }
          campusNames += campusData.find(campus => item === campus.campusCode)?.campusKorName;
        });
      }
    }

    arrSubTitle.push(t('common.excel.campus') + " : " + campusNames);

    exportTitleTable(getColumns(), data, `${t('statistics.regularTest.1')}_(${currentDate.format('YYYYMMDD')})`, t('statistics.regularTest.2'), [12, 12, 30, 15, 15, 15, 15, 12, 10, 10, 10, 12], excelTitle, arrSubTitle);
  }

  return (
    <>
      <div className="comm-bx__right">
        <div className="w-150p">
          <Select
            className="w-100"
            placeholder={t('student.student.column3')}
            value={gradeCode}
            onChange={value => setGradeCode(value)}
          >
            <Select.Option value="">
              {t('common.text.all')}
            </Select.Option>
            {SY0006?.map((item) => (
              <Select.Option
                key={item.detailCode}
                value={item.detailCode}
              >
                {item.korName}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>
      <div className='comm-table__area'>
        <Table
          columns={getColumns()}
          dataSource={data}
          pagination={{
            current: page,
            pageSize: config.pageSize,
          }}
          rowKey={'rowIdx'}
          rowClassName={(record: IExamStatsByLevel) => record.campusCode === '0' ? 'tfoot' : ''}
        />
      </div>
      <div className="comm-bx__between">
        <DownloadButton onClick={onExportTable} />
        <div className="common-paging center">
          <Pagination
            count={data.length}
            page={page}
            setPage={setPage}
          />
        </div>
      </div>
    </>
  );
}

export default ExamStatsByLevel;
